import { useNavigate, useParams } from 'react-router';
import type { InvitationDraft, UserPublic } from '@debtbot/shared';
import { useAcceptInvitation, useInvitationPreview } from '../api/hooks';
import { ApiError } from '../api/errors';
import { Button } from '../components/Button';
import { ErrorBanner } from '../components/ErrorBanner';
import { MainButtonFallback } from '../components/MainButtonFallback';
import { ListSkeleton } from '../components/Skeleton';
import { useT } from '../i18n';
import { capitalize, formatMoney, formatRelativeDay, fullName } from '../lib/format';
import { haptic } from '../telegram/haptic';
import { useMainButton } from '../telegram/ui';

/**
 * Первый экран приглашённого: кто зовёт (или карточка записи, если приглашение несёт черновик),
 * одна кнопка «Принять» (MainButton). После принятия — карточка человека; если записи ещё нет,
 * поверх неё сразу форма первой операции. Операция из черновика уже лежит в связи: если она
 * кажется неверной, её оспаривают там же, как любую другую.
 */
export function AcceptInvitationScreen() {
  const t = useT();
  const { token = '' } = useParams();
  const navigate = useNavigate();
  const preview = useInvitationPreview(token);
  const accept = useAcceptInvitation();

  const p = preview.data;
  const canAccept =
    !!p &&
    !p.is_self &&
    !p.existing_connection_id &&
    !p.inviter.is_deleted &&
    p.state === 'PENDING';

  const onAccept = async () => {
    if (!canAccept || accept.isPending) return;
    haptic.tap();
    try {
      const { connection, transaction_id } = await accept.mutateAsync(token);
      haptic.success();
      const base = `/c/${connection.id}`;
      void navigate(base, { replace: true });
      // Новая связь пуста — сразу открываем форму операции с предвыбранным контрагентом.
      if (!transaction_id && connection.balances.length === 0) void navigate(`${base}/new`);
    } catch (err) {
      haptic.error();
      if (err instanceof ApiError && err.code === 'INVITATION_EXPIRED') void preview.refetch();
    }
  };

  useMainButton({
    text: t.invite.accept,
    visible: canAccept,
    loading: accept.isPending,
    onClick: () => void onAccept(),
  });

  const goHome = () => void navigate('/', { replace: true });

  const wrap = (children: React.ReactNode) => (
    <main className="min-h-dvh bg-bg text-text px-4 py-4 pb-24 flex flex-col gap-4">
      {children}
    </main>
  );

  if (preview.isPending) return wrap(<ListSkeleton rows={2} />);
  if (preview.isError) {
    const notFound = preview.error instanceof ApiError && preview.error.status === 404;
    return wrap(
      notFound ? (
        <Message text={t.invite.notFound} onHome={goHome} />
      ) : (
        <ErrorBanner error={preview.error} onRetry={() => void preview.refetch()} />
      ),
    );
  }
  if (!p) return null;

  const name = fullName(p.inviter);

  if (p.is_self) return wrap(<Message text={t.invite.self} onHome={goHome} />);
  if (p.existing_connection_id) {
    const id = p.existing_connection_id;
    return wrap(
      <Message
        text={t.invite.alreadyConnected(name)}
        action={{
          label: t.invite.open,
          onClick: () => void navigate(`/c/${id}`, { replace: true }),
        }}
      />,
    );
  }
  if (p.inviter.is_deleted) return wrap(<Message text={t.invite.inviterGone} onHome={goHome} />);
  if (p.state === 'EXPIRED') return wrap(<Message text={t.invite.expired} onHome={goHome} />);
  if (p.state !== 'PENDING') return wrap(<Message text={t.invite.used} onHome={goHome} />);

  return wrap(
    <>
      <section className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
        {p.draft ? (
          <DraftCard inviter={p.inviter} draft={p.draft} createdAt={p.created_at} />
        ) : (
          <>
            <div className="text-5xl">🤝</div>
            <h1 className="text-2xl font-semibold">{t.invite.offer(name)}</h1>
          </>
        )}
        <p className="text-hint">{t.invite.body}</p>
        {accept.isError && <ErrorBanner error={accept.error} />}
      </section>
      <button
        type="button"
        data-testid="invite-decline"
        className="py-3 text-center text-link"
        onClick={goHome}
      >
        {t.invite.decline}
      </button>
      <MainButtonFallback
        text={t.invite.accept}
        disabled={accept.isPending}
        onClick={() => void onAccept()}
      />
    </>,
  );
}

/** Запись из черновика глазами приглашённого: payer=inviter → «Ты → Имя» (ты должен). */
function DraftCard({
  inviter,
  draft,
  createdAt,
}: {
  inviter: UserPublic;
  draft: InvitationDraft;
  createdAt: string;
}) {
  const t = useT();
  const name = fullName(inviter);
  const youOwe = draft.payer === 'inviter';
  return (
    <div
      data-testid="invite-draft-card"
      className="flex w-full flex-col items-center gap-2 rounded-2xl bg-secondary-bg px-6 py-6"
    >
      <h1 className="text-lg font-semibold">{t.invite.recorded(name)}</h1>
      <div className={`text-base ${youOwe ? 'text-negative' : 'text-positive'}`}>
        {youOwe ? t.invite.youToName(name) : t.invite.nameToYou(name)}
      </div>
      <div className="text-4xl font-semibold tabular-nums">
        {formatMoney(draft, { withCurrency: true })}
      </div>
      {draft.note && <div className="text-base">{draft.note}</div>}
      <div className="text-sm text-hint">{capitalize(formatRelativeDay(createdAt))}</div>
    </div>
  );
}

function Message({
  text,
  onHome,
  action,
}: {
  text: string;
  onHome?: () => void;
  action?: { label: string; onClick: () => void };
}) {
  const t = useT();
  return (
    <section className="flex flex-1 flex-col items-center justify-center gap-4 text-center">
      <p className="text-lg">{text}</p>
      {action && <Button onClick={action.onClick}>{action.label}</Button>}
      {onHome && (
        <Button variant={action ? 'secondary' : 'primary'} onClick={onHome}>
          {t.common.home}
        </Button>
      )}
    </section>
  );
}
